import type { NavigationGuard } from 'vue-router';
import { exchangeOidcAuthCode, exchangeSamlAuthCode, getUserProfile } from '@/api/auth';
import { scheduleOidcRefresh } from '@/composables/useOidcTokenRefresh';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { AIM_OIDC_ENABLED, ENVIRONMENT, PEQUITY_API_URL } from '@/types';
import {
  clearTokens,
  getCorrelationId,
  getUrlBase,
  setAccessToken,
  setIsOidcSession,
  setRefreshToken,
  TENANT,
} from '@/utils/helpers';

/**
 * This guard redirects the user to the SSO login page if the company is using SSO.
 */
export const redirectToSsoLoginIfUsingSso: NavigationGuard = async (to, from) => {
  if (to.query.error) {
    return;
  }

  const companyStore = useCompanyStore();

  if ((!TENANT && AIM_OIDC_ENABLED) || companyStore.isUsingOIDC) {
    if (from.name === 'oidc-login') {
      return;
    }
    window.location.href =
      ENVIRONMENT === 'local' ? PEQUITY_API_URL + '/oidc/authorize/' : getUrlBase() + '/api/oidc/authorize/';
    return false;
  }

  if (companyStore.isUsingSSO) {
    const authStore = useAuthStore();
    await authStore.setUserProfileAndLoggedInState();

    if (authStore.loggedIn) {
      return { name: 'home' };
    }

    const samlUrl = getUrlBase() + '/api/saml2/login';

    window.location.href = samlUrl;

    return false;
  }
};

/**
 * This guard redirects the user to the new tenant specific login page when the user tries to use the old link.
 * Eg. http://instacart.localhost:8080/login will be redirected to http://instacart.localhost:8080/welcome-back
 *
 */
export const handlePreviousTenantSpecificLoginRoute: NavigationGuard = async (to, from, next) => {
  if (TENANT) {
    return next({ name: 'welcome-back' });
  }

  return next();
};

/**
 * This guard handles the SAML login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleSamlLoginRedirect: NavigationGuard = async (to, from, next) => {
  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  // Exchange the one-time auth code for tokens via POST request
  const { data, error: exchangeError } = await exchangeSamlAuthCode(code);

  if (exchangeError || !data?.access_token || !data?.refresh_token) {
    return next({ name: 'welcome-back' });
  }

  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);

  const { error } = await getUserProfile();

  if (error) {
    return next({ name: 'welcome-back' });
  } else {
    return next({ name: 'home' });
  }
};

/**
 * This guard handles the Lyric → Pequity OIDC SSO login flow.
 * It exchanges the one-time auth code for tokens via a secure POST request,
 * then sets them in local storage and redirects to the home page.
 */
export const handleOidcLoginRedirect: NavigationGuard = async (to, from, next) => {
  if (to.query.error) {
    return next({ name: 'welcome-back', query: { error: to.query.error } });
  }

  const code = to.query.code;

  if (!code || typeof code !== 'string') {
    return next({ name: 'welcome-back' });
  }

  const { data, error: exchangeError } = await exchangeOidcAuthCode(code);

  if (exchangeError || !data?.access_token) {
    return next({ name: 'welcome-back' });
  }

  setAccessToken(data.access_token);
  setIsOidcSession();

  const authStore = useAuthStore();
  await authStore.setUserProfileAndLoggedInState();

  if (authStore.loggedIn) {
    scheduleOidcRefresh();
    return next({ name: 'home' });
  } else {
    clearTokens();
    return next({ name: 'welcome-back' });
  }
};

export const goHomeIfNoCorrelationId: NavigationGuard = async (to, from, next) => {
  if (getCorrelationId() === null) {
    return next({ name: 'home' });
  }
  return next();
};
